import { useNavigate } from "react-router-dom";
import React from "react";
import { useOffer } from "hooks/useOffer";

interface CallToActionButtonProps {
    applicantType: "member" | "spouse";
    memberOfferAvailable: boolean;
    spouseOfferAvailable: boolean;
    navigateTo?: string;
    href?: string;
}

const CallToActionButton = ({
    applicantType,
    memberOfferAvailable,
    spouseOfferAvailable,
    navigateTo,
    href,
}: CallToActionButtonProps) => {
    // ========== Hooks ==========
    const navigate = useNavigate();
    const { setMemberOfferAvailable, setSpouseOfferAvailable, resetApplicationKeepOffer } = useOffer();

    // ========== Computed Values ==========
    const showSpouseApplication = href ? true : applicantType === "member" && spouseOfferAvailable;
    const showMemberApplication = applicantType === "spouse" && memberOfferAvailable;
    const showApplicationButton = showSpouseApplication || showMemberApplication;

    const callToActionText = showSpouseApplication
        ? "Would you like to start an application to cover your spouse?"
        : "Would you like to begin your application?";

    const buttonText = showSpouseApplication ? "Begin Spouse Application" : "Get Coverage";

    // ========== Event Handlers ==========
    const handleClick = () => {
        // Reset application state while keeping offer data
        resetApplicationKeepOffer();

        // Update offer availability so Quote can switch tab to the corresponding applicant
        if (showSpouseApplication) {
            setMemberOfferAvailable(false);
        } else if (showMemberApplication) {
            setSpouseOfferAvailable(false);
        }

        if (navigateTo) {
            navigate(navigateTo);
        }
    };

    // Navigate programmatically so GA's cross-domain auto-linker can't decorate the
    // URL with _gl/_ga/_gcl_au params (it only rewrites real anchor clicks).
    const handleExternalClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (!href) {
            return;
        }
        event.preventDefault();
        window.location.href = href;
    };

    // ========== Render ==========
    if (!showApplicationButton) {
        return null;
    }

    const buttonClassName =
        "btn bg-secondary-content font-semibold flex text-sm text-secondary border-none rounded hover:bg-secondary-content/80 min-h-0 h-[36px] px-6";

    return (
        <>
            <p className="font-medium text-sm leading-5">{callToActionText}</p>

            <div className="flex justify-center items-center w-full">
                {href ? (
                    <a href={href} onClick={handleExternalClick} className={buttonClassName}>
                        {buttonText}
                    </a>
                ) : (
                    <button onClick={handleClick} className={buttonClassName}>
                        {buttonText}
                    </button>
                )}
            </div>
        </>
    );
};

export default CallToActionButton;
